import { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import { cn } from '@/lib/utils';
import { AuthForm } from './AuthForm';
import { useVisualViewportInset } from '@/hooks/useVisualViewportInset';

interface AuthSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	intentRole?: 'buyer' | 'seller';
	/** Called after a successful sign-in / sign-up, before the sheet closes. */
	onAuthed?: () => void;
}


/**
 * AuthSheet renders the same glass <Dialog> on both mobile and desktop.
 * Built on Radix primitives directly (not the wrapped DialogContent) because
 * the shared DialogContent forces a solid bottom-sheet on mobile.
 */
export const AuthSheet = ({ open, onOpenChange, intentRole, onAuthed }: AuthSheetProps) => {
	const [isPhone, setIsPhone] = useState<boolean>(() =>
		typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
	);
	useEffect(() => {
		const mql = window.matchMedia('(max-width: 767px)');
		const onChange = () => setIsPhone(mql.matches);
		mql.addEventListener('change', onChange);
		setIsPhone(mql.matches);
		return () => mql.removeEventListener('change', onChange);
	}, []);
	const { keyboardHeight } = useVisualViewportInset();

	const handleAuthed = () => {
		onAuthed?.();
		onOpenChange(false);
	};

	const handleMagicSent = () => {
		onOpenChange(false);
	};

	return (
		<DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay
					className={cn(
						'fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]',
						'data-[state=open]:animate-in data-[state=closed]:animate-out',
						'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
					)}
				/>
				<DialogPrimitive.Content
					className={cn(
						// Centered on both viewports
						'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
						// Mobile: full screen width, flush to edges. Desktop: 440px floating card.
						'w-screen md:w-[calc(100vw-1.5rem)] md:max-w-[440px]',
						// Square edges on mobile so the glass reads as a band, rounded card on desktop
						'overflow-hidden rounded-none md:rounded-2xl',
						// Top/bottom hairline only on mobile (sides sit at screen edge); full border on desktop
						'border-y md:border',
						'bg-black/55 backdrop-blur-2xl border-white/[0.1] shadow-2xl',
						'shadow-[inset_0_1px_1px_rgba(255,255,255,0.04)] text-foreground',
						// Motion
						'duration-200 ease-out',
						'data-[state=open]:animate-in data-[state=closed]:animate-out',
						'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
						'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
					)}
				>
					<VisuallyHidden>
						<DialogPrimitive.Title>Sign in to CardSimp</DialogPrimitive.Title>
						<DialogPrimitive.Description>
							Continue with email and password or request a magic link.
						</DialogPrimitive.Description>
					</VisuallyHidden>

					{/* X close — desktop only; mobile dismisses via backdrop tap */}
					<DialogPrimitive.Close
						type="button"
						aria-label="Close"
						className={cn(
							'absolute right-3 top-3 z-10 hidden md:inline-flex h-9 w-9 items-center justify-center',
							'rounded-full text-foreground/70 hover:text-foreground',
							'hover:bg-white/[0.06] active:bg-white/[0.1]',
							'focus:outline-none focus-visible:ring-1 focus-visible:ring-white/30',
							'[-webkit-tap-highlight-color:transparent]',
						)}
					>
						<X className="h-5 w-5" />
					</DialogPrimitive.Close>

					{/* Scroll body — caps height + pads past the keyboard so OAuth row stays reachable */}
					<div
						className="overflow-y-auto overscroll-contain px-6 pt-7 pb-6"
						style={{
							maxHeight: keyboardHeight
								? `calc(100dvh - ${keyboardHeight}px - 24px)`
								: '85dvh',
							paddingBottom: keyboardHeight ? '1.5rem' : undefined,
						}}
					>
						<AuthForm
							intentRole={intentRole}
							onAuthed={handleAuthed}
							onMagicSent={handleMagicSent}
							autoFocusEmail={!isPhone && open}
						/>
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
};

export default AuthSheet;
